import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { msalInstance } from 'src/msalConfig';
import cn from 'classnames';
import { capitalize, debounce, groupBy } from 'lodash';
// components
import { Link } from 'react-router-dom';
import Icon from 'shared/icons/IconComponent';
import BaseIcon from 'shared/icons/BaseIcon';
import Button from 'shared/buttons';
import Input from 'shared/inputs/BaseInput';
import { Slide } from '@progress/kendo-react-animation';
// Hooks
import useNavigation from './useNavigation';
import useTimer from 'shared/../hooks/useTimer';
import { useOnClickOutside } from 'shared/../hooks';
// Styling
import './style.scss';
import { theme } from 'src/theme';
import useCurrentUser from '../../../hooks/useCurrentUser';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const NavItem = ({
  children, path, className, isActive, userAccess, bypassLanding, ...props
}) => {
  const linkClassName = cn('nav-item', { active: isActive, [className]: className });

  return (
    <Link className={linkClassName} to={`${path}`} {...props}>
      {children}
    </Link>
  );
};

const MenuItem = ({
  text, isActive, subNav, icon, onCollapse, onMouseEnter, onMobClick, expanded, ...props
}) => {
  const hasSubNav = subNav && subNav?.length > 0;
  const menuItemClassName = cn('menu-item', { expandable: hasSubNav });

  const { reset, start } = useTimer({ expiry: 500, onExpire: onMouseEnter, autoStart: false });

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        role='button'
        tabIndex={0}
        className='menu-item-container'
        onClick={onMobClick}
        onMouseEnter={start}
        onMouseLeave={reset}
      >
        <NavItem className={menuItemClassName} isActive={isActive} {...props}>
          <Icon className='menu-item-icon' icon={icon} />
          {capitalize(text)}
          {hasSubNav && (
            <div className='collapse-item-container'>
              <BaseIcon
                name={!expanded ? 'arrow-chevron-down' : 'arrow-chevron-up'}
              />
            </div>
          )}
        </NavItem>
      </div>
      <Slide>
        {expanded && hasSubNav && (
          <div role="navigation" className='menu-item-sub-nav' onClick={onCollapse}>
            {subNav.map((item) => <SubMenuItem key={item.id} {...item} />)}
          </div>
        )}
      </Slide>
    </div>
  );
};

const SubMenuItem = ({ text, icon, ...props }) => (
  <NavItem className='sub-nav-item' {...props}>
    {icon}
    {text}
  </NavItem>
);

const UserSubMenu = ({ systemUserName, version, onLogout }) => {
  const { stopImpersonate } = useCurrentUser();
  const resetImpersonation = () => {
    stopImpersonate();
    window.location.reload();
  };

  const isImpersonating = sessionStorage.getItem('impersonate');

  return (
    <div className='user-sub-menu'>
      <div className='user-name-wrapper'>{systemUserName}</div>
      <div>{version}</div>
      <div>
        <Button onClick={isImpersonating ? resetImpersonation : onLogout} fillMode='flat' icon='logout'>
          {isImpersonating ? 'Reset Impersonation' : 'Logout'}
        </Button>
      </div>
    </div>
  );
};

const UserMenu = ({ name, onMouseEnter, ...props }) => (
  <div className='user-menu-container'>
    <div className='nav-item menu-item user-menu' onMouseEnter={onMouseEnter}>
      <span className='k-icon k-i-user menu-item-icon' />
      <div>{sessionStorage.getItem('impersonate') ? `Impersonating ${JSON.parse(sessionStorage.getItem('impersonate'))?.displayName}` : name}</div>
    </div>
    <UserSubMenu {...props} />
  </div>
);

const CollapseButton = ({ onClick }) => (
  <div className='collapse-button-container'>
    <Button onClick={onClick} themeColor='primary' rounded='full' icon="arrow-chevron-left" />
  </div>
);

const SideNavMenu = ({ isCollapsed, version, onCollapse }) => {
  const {
    navItems,
    subNavItems,
    openNavPath,
    showSubNav,
    closeSubNav,
    show,
    userFullName,
    systemUserName,
    // Search
    searchList,
    getSectionTitle,
  } = useNavigation();

  const onLogout = () => msalInstance.logoutRedirect();

  // Search through navigation
  const [isSearching, setIsSearching] = useState(false);
  const [foundNavs, setFoundNavs] = useState(searchList);
  const [searchText, setSearchText] = useState('');
  const menuRef = useRef();

  const searchCallback = useCallback(
    debounce(() => {
      if (!searchText || searchText.trim() === '') {
        setFoundNavs(searchList);
      } else {
        setFoundNavs(searchList
          .filter(({ text }) => text.toLowerCase().includes(searchText.toLowerCase())));
      }
    }, 300),
    [searchList, searchText],
  );

  useEffect(() => {
    searchCallback();
  }, [searchCallback]);

  const groupedFound = groupBy(foundNavs, 'section');

  const resetSearchWithResult = () => {
    setIsSearching(false);
    setSearchText('');
    onCollapse();
  };
  // Close and reset search on click outside
  useOnClickOutside(menuRef, resetSearchWithResult);

  const searchClassName = cn('main-nav-search', { 'main-nav-search-active': isSearching });

  // Closing sub nav
  const { reset, start } = useTimer({ expiry: 1000, onExpire: closeSubNav });

  const hasSubNav = subNavItems && subNavItems.length > 0;
  const menuClassName = cn('side-nav-menu', {
    collapsed: isCollapsed,
    'has-sub-nav': hasSubNav,
    'search-mode': isSearching,
  });

  const onHoverHandler = ({ path, subNav }) => () => {
    reset();
    if (openNavPath !== path && (subNav || path === '_user')) {
      showSubNav(path);
    }
  };

  // Mob menu collapsed items
  const [expanded, setExpanded] = useState(null);
  const onMobClickHandler = ({ path }) => () => setExpanded((prev) => (prev === path ? null : path));

  return (
    show && (
      <div ref={menuRef} className={menuClassName} onMouseLeave={start}>
        <div className='main-nav'>
          <CollapseButton onClick={onCollapse} />
          <div className={searchClassName}>
            <Input
              icon='search'
              className='main-nav-search-input'
              onFocus={() => setIsSearching(true)}
              onChange={({ value }) => setSearchText(value)}
              value={searchText}
              placeholder='Search...'
              actionComponent={(
                <Button
                  icon='x'
                  themeColor='tertiary'
                  fillMode='flat'
                  onClick={resetSearchWithResult}
                />
              )}
            />
            {isSearching && Object.keys(groupedFound).map((sectionPath) => (
              <div key={sectionPath} className='nav-search-found'>
                <div className='found-nav'>
                  <NavItem to={sectionPath} onClick={resetSearchWithResult}>{getSectionTitle(sectionPath)}</NavItem>
                  <Button
                    icon='hyperlink-open-sm'
                    themeColor='tertiary'
                    fillMode='flat'
                    tooltip='Open in a new tab'
                    onClick={() => window.open(sectionPath, '_blank')}
                  />
                </div>
                {groupedFound[sectionPath].map(({ path, icon, text }) => (
                  <div key={path} className='found-sub-nav'>
                    <NavItem className='sub-nav-item' to={path} onClick={resetSearchWithResult}>
                      {icon}
                      {text}
                    </NavItem>
                    <Button
                      icon='hyperlink-open-sm'
                      themeColor='tertiary'
                      fillMode='flat'
                      tooltip='Open in a new tab'
                      onClick={() => window.open(path, '_blank')}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!isSearching && (
            <>
              {navItems.map((item) => (
                <MenuItem
                  key={item.id}
                  onCollapse={onCollapse}
                  onMouseEnter={onHoverHandler(item)}
                  expanded={item.path === expanded}
                  onMobClick={item.subNav && item.subNav?.length > 0 ? onMobClickHandler(item) : undefined}
                  {...item}
                />
              ))}
              <UserMenu
                name={userFullName}
                onMouseEnter={onHoverHandler({ path: '_user' })}
                systemUserName={systemUserName}
                version={version?.header}
                onLogout={onLogout}
              />
            </>
          )}
        </div>
        {!isSearching && (
          <>
            {hasSubNav && (
              <div role="navigation" className='sub-nav' onMouseEnter={reset}>
                {subNavItems.map((item) => <SubMenuItem key={item.id} {...item} />)}
              </div>
            )}
            {openNavPath === '_user' && (
              <div className='sub-nav' onMouseEnter={reset}>
                <UserSubMenu systemUserName={systemUserName} version={version?.header} onLogout={onLogout} />
              </div>
            )}
          </>
        )}
      </div>
    )
  );
};

export default SideNavMenu;
